import { Badge, Box, Button, ButtonGroup, FormControl, FormLabel, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useDisclosure, useTheme } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import BackButton from "../../components/BackButton";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import { MapPin } from "../../components/Map";
import QueryStateWrapper from "../../components/QueryStateWrapper";
import { useAttendanceEventById } from "../../data/attendance.api";
import { useFocusOnPoints } from "../../hooks/useFocusOnPoints";

type FilterType = "successful" | "failed";

// Utility function to calculate distance between two coordinates using Haversine formula
const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in kilometers
};

const AttendanceForensics = () => {
  const { eventIdentity: urlEventIdentity } = useParams<{
    eventIdentity: string;
  }>();
  const [manualEventIdentity, setManualEventIdentity] = useState<string>("");
  const [filter, setFilter] = useState<FilterType>("successful");

  // State to control when searches are executed
  const [executeEventIdSearch, setExecuteEventIdSearch] = useState<
    string | null
  >(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalData, setModalData] = useState<{
    type: string;
    coordinates: { lat: number | undefined; lng: number | undefined };
    distance: number | null;
    scheduledLocation: { description: string; lat: number; lng: number } | null;
    attemptNumber?: number;
  } | null>(null);
  const theme = useTheme();

  // Clear event identity execution state when manual input changes
  useEffect(() => {
    if (!urlEventIdentity) {
      setExecuteEventIdSearch(null);
    }
  }, [manualEventIdentity, urlEventIdentity]);

  // Use URL parameter if available, otherwise use executed search
  const eventIdentity = urlEventIdentity || executeEventIdSearch;

  // Fetch data based on event identity
  const {
    data: eventIdData,
    loading: eventIdLoading,
    error: eventIdError,
  } = useAttendanceEventById(eventIdentity || null);

  const attendanceEvents = useMemo(() => {
    let events: any[] = [];
    if (eventIdData) {
      events = eventIdData.getAttendanceEventById || [];
    }
    console.log("Raw attendance events:", events.length, events);
    return events;
  }, [eventIdData]);

  // Search functions
  const handleEventIdentitySearch = () => {
    if (manualEventIdentity.trim()) {
      setExecuteEventIdSearch(manualEventIdentity.trim());
    }
  };

  const handleKeyPress = (
    event: React.KeyboardEvent,
    searchFunction: () => void
  ) => {
    if (event.key === "Enter") {
      searchFunction();
    }
  };

  // Reset function to clear all search states
  const resetSearch = () => {
    setExecuteEventIdSearch(null);
    setManualEventIdentity("");
  };

  // Show loading state immediately when search parameters change
  const isLoading = eventIdLoading && !!eventIdentity;
  const hasError = eventIdError && !!eventIdentity;

  // Filter events based on successful vs failed check-ins
  const filteredEvents = useMemo(() => {
    switch (filter) {
      case "successful":
        return attendanceEvents.filter((event) => event.successfulAttempt);
      case "failed":
        return attendanceEvents.filter(
          (event) => event.failedAttempts && event.failedAttempts.length > 0
        );
      default:
        return attendanceEvents.filter((event) => event.successfulAttempt);
    }
  }, [attendanceEvents, filter]);

  // Map attendance events to map pins
  const points = useMemo(() => {
    const pins: MapPin[] = [];

    console.log("Processing filteredEvents:", filteredEvents.length, "events");

    // Get the scheduled/registered location coordinates for distance calculation
    const scheduledLocation =
      filteredEvents.length > 0 &&
      filteredEvents[0].locations &&
      filteredEvents[0].locations.length > 0
        ? {
            lat: parseFloat(filteredEvents[0].locations[0].Latitude || "0"),
            lng: parseFloat(filteredEvents[0].locations[0].Longitude || "0"),
            description:
              filteredEvents[0].locations[0].Description ||
              "Scheduled Location",
          }
        : null;

    filteredEvents.forEach((event, eventIndex) => {
      console.log(`Processing event ${eventIndex}:`, event.eventDescription);

      // Add successful attempt location
      if (
        event.successfulAttempt?.latitude &&
        event.successfulAttempt?.longitude
      ) {
        const lat = event.successfulAttempt.latitude;
        const lng = event.successfulAttempt.longitude;
        console.log(`Adding successful attempt pin: lat=${lat}, lng=${lng}`);

        // Calculate distance from scheduled location
        const distance =
          scheduledLocation &&
          !isNaN(scheduledLocation.lat) &&
          !isNaN(scheduledLocation.lng)
            ? calculateDistance(
                lat,
                lng,
                scheduledLocation.lat,
                scheduledLocation.lng
              )
            : null;

        pins.push({
          lat,
          lng,
          color: theme?.colors?.["green"]?.["500"] || "#22c55e",
          onClick: () => {
            setModalData({
              type: "Successful Check-in",
              coordinates: { lat, lng },
              distance,
              scheduledLocation,
            });
            onOpen();
          },
        });
      }

      // Add failed attempt locations
      if (event.failedAttempts && event.failedAttempts.length > 0) {
        console.log(
          `Processing ${event.failedAttempts.length} failed attempts`
        );

        event.failedAttempts.forEach((failedAttempt: any, index: number) => {
          const geolocation =
            failedAttempt.geolocation || failedAttempt.Geolocation;
          let lat: number | undefined;
          let lng: number | undefined;

          if (geolocation) {
            // Handle both Geolocation and FailedAttemptGeolocation types
            lat =
              (geolocation as any).latitude || (geolocation as any).Latitude;
            lng =
              (geolocation as any).longitude || (geolocation as any).Longitude;
          }

          if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
            console.log(
              `Adding failed attempt pin ${index}: lat=${lat}, lng=${lng}`
            );

            // Calculate distance from scheduled location
            const distance =
              scheduledLocation &&
              !isNaN(scheduledLocation.lat) &&
              !isNaN(scheduledLocation.lng)
                ? calculateDistance(
                    lat,
                    lng,
                    scheduledLocation.lat,
                    scheduledLocation.lng
                  )
                : null;

            pins.push({
              lat,
              lng,
              onClick: () => {
                setModalData({
                  type: "Failed Check-in Attempt",
                  coordinates: { lat, lng },
                  distance,
                  scheduledLocation,
                  attemptNumber: index + 1,
                });
                onOpen();
              },
              color: theme?.colors?.["red"]?.["500"] || "#ef4444",
            });
          } else {
            console.log(
              `Skipping failed attempt ${index}: invalid coordinates`,
              { lat, lng }
            );
          }
        });
      }

      // Add event locations (registered locations)
      if (event.locations && event.locations.length > 0) {
        console.log(
          `Processing ${event.locations.length} registered locations`
        );

        event.locations.forEach((location: any, index: number) => {
          if (location.Latitude && location.Longitude) {
            const lat = parseFloat(location.Latitude);
            const lng = parseFloat(location.Longitude);

            if (!isNaN(lat) && !isNaN(lng)) {
              console.log(
                `Adding registered location pin ${index}: lat=${lat}, lng=${lng}, desc=${location.Description}`
              );

              pins.push({
                lat,
                lng,
                variant: "blueDot",
                accuracy: 20, // 20 meters radius per requirement
                onClick: () => {
                  setModalData({
                    type: "Registered Location",
                    coordinates: { lat, lng },
                    distance: null,
                    scheduledLocation: {
                      description: location.Description || "Unknown",
                      lat,
                      lng,
                    },
                  });
                  onOpen();
                },
              });
            } else {
              console.log(
                `Skipping registered location ${index}: invalid coordinates`,
                { lat, lng }
              );
            }
          } else {
            console.log(
              `Skipping registered location ${index}: missing coordinates`,
              location
            );
          }
        });
      }
    });

    console.log("Total pins generated:", pins.length);
    console.log("Pins array:", pins);

    // Add a test pin to verify map functionality
    if (pins.length === 0 && filteredEvents.length > 0) {
      console.log("Adding test pin for debugging");
      pins.push({
        lat: 54.008355, // Lancaster University coordinates
        lng: -2.785908,
        onClick: () => {
          setModalData({
            type: "Test Pin",
            coordinates: { lat: 54.008355, lng: -2.785908 },
            distance: null,
            scheduledLocation: null,
          });
          onOpen();
        },
      });
    }

    return pins;
  }, [filteredEvents, theme?.colors, onOpen, setModalData]);

  useFocusOnPoints(points);

  const successfulCount = attendanceEvents.filter(
    (event) => event.successfulAttempt
  ).length;
  const failedCount = attendanceEvents.filter(
    (event) => event.failedAttempts && event.failedAttempts.length > 0
  ).length;

  return (
    <HybridBottomSheetDrawer>
      <BackButton />

      <Box p={8}>
        <Heading size="md" mb={4}>
          Check-ins for Event
        </Heading>

        {!urlEventIdentity && (
          <Box mb={6}>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Event Identity</FormLabel>
                <Input
                  placeholder="Enter event identity..."
                  value={manualEventIdentity}
                  onChange={(e) => setManualEventIdentity(e.target.value)}
                  onKeyPress={(e) =>
                    handleKeyPress(e, handleEventIdentitySearch)
                  }
                />
              </FormControl>
              <Stack direction="row" spacing={2}>
                <Button
                  colorScheme="blue"
                  onClick={handleEventIdentitySearch}
                  disabled={!manualEventIdentity.trim()}
                  flex={1}
                >
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={resetSearch}
                  disabled={!executeEventIdSearch && !manualEventIdentity}
                >
                  Clear
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {!!eventIdentity && (
          <>
            {hasError &&
              eventIdError?.message?.includes("Can't serialize value") && (
                <Box
                  mb={4}
                  p={4}
                  bg="yellow.50"
                  borderRadius="md"
                  borderWidth={1}
                  borderColor="yellow.200"
                >
                  <Text fontSize="sm" color="yellow.800">
                    <strong>Data Format Warning:</strong> Some attendance data
                    contains invalid category values and cannot be displayed.
                    The query has been modified to show available data.
                  </Text>
                </Box>
              )}
            <QueryStateWrapper
              isLoading={isLoading}
              error={hasError}
              data={eventIdData}
            >
              <Box mb={6}>
                <ButtonGroup size="sm" isAttached variant="outline">
                  <Button
                    colorScheme={filter === "successful" ? "green" : "gray"}
                    onClick={() => setFilter("successful")}
                  >
                    Successful ({successfulCount})
                  </Button>
                  <Button
                    colorScheme={filter === "failed" ? "red" : "gray"}
                    onClick={() => setFilter("failed")}
                  >
                    Failed ({failedCount})
                  </Button>
                </ButtonGroup>
              </Box>

              {filteredEvents.length > 0 ? (
                <Stack spacing={4}>
                  {filteredEvents.map((event, index) => (
                    <Box
                      key={`${event.eventIdentity}-${index}`}
                      p={4}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      <Stack spacing={2}>
                        <Box>
                          <Text fontWeight="bold">
                            {event.eventDescription}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {event.firstName} {event.surname} ({event.username})
                          </Text>
                        </Box>

                        <Box>
                          <Badge
                            colorScheme={event.present ? "green" : "red"}
                            mr={2}
                          >
                            {event.present ? "Present" : "Absent"}
                          </Badge>
                          {event.cancelled && (
                            <Badge colorScheme="orange" mr={2}>
                              Cancelled
                            </Badge>
                          )}
                          {event.blacklisted && (
                            <Badge colorScheme="red" mr={2}>
                              Blacklisted
                            </Badge>
                          )}
                        </Box>

                        {event.successfulAttempt && (
                          <Box>
                            <Text
                              fontSize="sm"
                              fontWeight="semibold"
                              color="green.600"
                            >
                              Successful Check-in:
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              Lat: {event.successfulAttempt.latitude}, Lng:{" "}
                              {event.successfulAttempt.longitude}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              Accuracy: {event.successfulAttempt.accuracy}m
                            </Text>
                          </Box>
                        )}

                        {event.failedAttempts &&
                          event.failedAttempts.length > 0 && (
                            <Box>
                              <Text
                                fontSize="sm"
                                fontWeight="semibold"
                                color="red.600"
                              >
                                Failed Attempts ({event.failedAttempts.length}):
                              </Text>
                              {event.failedAttempts
                                .slice(0, 3)
                                .map((attempt: any, attemptIndex: number) => {
                                  const geolocation = attempt.geolocation;
                                  console.log("failed attempt:", attempt);
                                  const lat = geolocation?.latitude;
                                  const lng = geolocation?.longitude;
                                  return (
                                    <Box
                                      key={attemptIndex}
                                      fontSize="xs"
                                      color="gray.600"
                                      ml={2}
                                    >
                                      <Text>
                                        Lat: {lat || "N/A"}, Lng: {lng || "N/A"}
                                      </Text>
                                      <Text>
                                        Reason: {attempt.Category || "Unknown"}
                                      </Text>
                                    </Box>
                                  );
                                })}
                              {event.failedAttempts.length > 3 && (
                                <Text fontSize="xs" color="gray.500" ml={2}>
                                  ... and {event.failedAttempts.length - 3} more
                                </Text>
                              )}
                            </Box>
                          )}

                        {event.locations && event.locations.length > 0 && (
                          <Box>
                            <Text
                              fontSize="sm"
                              fontWeight="semibold"
                              color="blue.600"
                            >
                              Registered Locations:
                            </Text>
                            {event.locations.map(
                              (location: any, locationIndex: number) => (
                                <Box key={locationIndex} ml={2}>
                                  <Text fontSize="xs" color="gray.600">
                                    📍 {location.Description} (
                                    {location.Latitude}, {location.Longitude})
                                  </Text>
                                </Box>
                              )
                            )}
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500">
                    No {filter} check-ins found for this event.
                  </Text>
                </Box>
              )}
            </QueryStateWrapper>
          </>
        )}

        {!eventIdentity && (
          <Box textAlign="center" py={8}>
            <Text color="gray.500">
              Enter an event identity and click Search to view check-ins.
            </Text>
          </Box>
        )}
      </Box>

      {/* Distance Information Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Location Information</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {modalData && (
              <Stack spacing={3}>
                <Box>
                  <Text fontWeight="bold" color="blue.600">
                    {modalData.type}
                    {modalData.attemptNumber && ` ${modalData.attemptNumber}`}
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="semibold">
                    Coordinates:
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Latitude: {modalData?.coordinates?.lat?.toFixed(6)}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Longitude: {modalData?.coordinates?.lng?.toFixed(6)}
                  </Text>
                </Box>

                {modalData.distance !== null && modalData.scheduledLocation && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold">
                      Distance from Scheduled Location:
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      <strong>{modalData.distance.toFixed(2)} km</strong> from{" "}
                      {modalData.scheduledLocation.description}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Scheduled location:{" "}
                      {modalData.scheduledLocation.lat.toFixed(6)},{" "}
                      {modalData.scheduledLocation.lng.toFixed(6)}
                    </Text>
                  </Box>
                )}

                {modalData.type === "Registered Location" && (
                  <Box>
                    <Text fontSize="sm" color="blue.600">
                      This is the official scheduled location for the event.
                    </Text>
                  </Box>
                )}
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </HybridBottomSheetDrawer>
  );
};

export default AttendanceForensics;
